import * as React from "react";
import PropTypes from "prop-types";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import {Alert, AppBar, Card, NativeSelect} from "@mui/material";
import {FormControl, InputLabel, MenuItem, Select} from "@mui/material";
import MDButton from "components/MDButton";
import {POST, GET} from "APIclient";
import dayjs from "dayjs";
import {useState, useEffect} from "react";
import {DemoContainer} from "@mui/x-date-pickers/internals/demo";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import Snackbar from "@mui/material/Snackbar";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {useLocation, useNavigate} from "react-router-dom";
import properties from "constants/ApplicationConstants";

export default function AddClient(props) {
  const location = useLocation();
  const [usersState, setUsersState] = useState(location.state);
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [snakColor, setSnakColor] = useState("");
  const [snakMessage, setSnakMessage] = useState("");
  const [clients, setClients] = useState([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [formData, setFormData] = useState({
    clientName: "",
    clientWebsite: "",
    clientType: "",
    contactNumber: ""
  });
  const [errors, setErrors] = useState({
    clientName: "",
    clientType: ""
  });

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const clientResponse = await GET("admin/client-list");
        setClients(clientResponse.data?.data?.paginatedData || []);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    fetchClient();
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case "clientName":
        return value.trim() ? "" : "Client Name is required";
      case "clientType":
        return value ? "" : "Client Type is required";
      default:
        return "";
    }
  };

  const handleChange = e => {
    const {name, value} = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    const newErrors = {
      clientName: validateField("clientName", formData.clientName),
      clientType: validateField("clientType", formData.clientType)
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some(error => error)) {
      setSnakColor("error");
      setSnakMessage("Please fill all required fields.");
      setOpen(true);
      return;
    }

    const url = `admin/add-client`;
    const dt = {
      clientName: formData.clientName,
      clientWebsite: formData.clientWebsite,
      clientType: formData.clientType,
      contactNumber: formData.contactNumber
    };

    try {
      const response = await POST(url, dt);
      const data = response.data;
      if (data.statusCode === 200) {
        setSnakColor("success");
        setSnakMessage("Client Added successfully");
        setOpen(true);
        setTimeout(() => {
          navigate("/clientList");
        }, 1500);
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      const message = error?.response?.data?.message || "Failed to add client";
      if (statusCode === 409) {
        setSnakColor("error");
        setSnakMessage("Client name already exists.");
      } else if (statusCode === 400) {
        setSnakColor("error");
        setSnakMessage("Bad request. Please check your inputs.");
      } else if (statusCode === 500) {
        setSnakColor("error");
        setSnakMessage("Server error. Please try again later.");
      } else {
        setSnakColor("error");
        setSnakMessage(message);
      }
      setOpen(true);
    }
  };

  useEffect(() => {
    setIsSaveDisabled(!(formData.clientName.trim() && formData.clientType));
  }, [formData.clientName, formData.clientType]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox
              pt={2}
              mb={0}
              pb={4}
              px={4}
              sx={{borderRadius: "5px", border: "1px solid #D3D3D3"}}
            >
              <Typography
                sx={{
                  color: "textPrimary",
                  fontFamily: properties.fontFamily,
                  fontSize: "16px",
                  pb: 2,
                  fontWeight: "bold"
                }}
                id="client"
              >
                Add Client
              </Typography>
              <MDInput
                sx={{m: 1, pb: 2, width: "30ch"}}
                name="clientName"
                value={formData.clientName || ""}
                type="text"
                label={
                  <span>
                    Client Name <span style={{color: properties.colors.Red}}>*</span>
                  </span>
                }
                variant="standard"
                onChange={handleChange}
                error={!!errors.clientName}
                helperText={errors.clientName}
              />

              <FormControl variant="standard" sx={{m: 2, width: "30ch"}}>
                <InputLabel id="clientType">
                  Client Type <span style={{color: properties.colors.Red}}>*</span>
                </InputLabel>
                <Select
                  value={formData.clientType || ""}
                  name="clientType"
                  label="Client Type"
                  onChange={handleChange}
                  IconComponent={ExpandMoreIcon}
                  error={!!errors.clientType}
                >
                  <MenuItem value="">
                    <em>Select</em>
                  </MenuItem>
                  <MenuItem value="Ind">Internal</MenuItem>
                  <MenuItem value="Us">External</MenuItem>
                </Select>
                {errors.clientType && (
                  <MDBox sx={{color: properties.colors.Red, fontSize: "0.75rem", mt: 0.5}}>
                    {errors.clientType}
                  </MDBox>
                )}
              </FormControl>

              <MDInput
                sx={{m: 1, width: "30ch"}}
                type="text"
                value={formData.clientWebsite || ""}
                name="clientWebsite"
                variant="standard"
                label="Website"
                onChange={handleChange}
              />
              <MDInput
                sx={{m: 1, width: "30ch"}}
                type="text"
                value={formData.contactNumber || ""}
                name="contactNumber"
                variant="standard"
                label="Contact Number"
                onChange={handleChange}
              />
            </MDBox>

            <MDBox mt={3} mb={2} sx={{display: "flex", justifyContent: "flex-end"}}>
              <MDBox sx={{display: "flex", gap: 1}}>
                <MDButton
                  variant="gradient"
                  color="warning"
                  sx={{width: "20ch"}}
                  onClick={() => navigate(-1)}
                >
                  Back
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  sx={{
                    marginRight: "10px",
                    width: "20ch",
                    background: properties.colors.bgcolorbluethem
                  }}
                  onClick={handleSubmit}
                  disabled={isSaveDisabled}
                >
                  Save
                </MDButton>
              </MDBox>
            </MDBox>
            <Snackbar
              open={open}
              anchorOrigin={{vertical: "top", horizontal: "right"}}
              autoHideDuration={6000}
              onClose={handleClose}
            >
              <Alert
                autoHideDuration={1200}
                onClose={handleClose}
                severity={snakColor}
                variant="filled"
                sx={{width: "100%"}}
              >
                {snakMessage}
              </Alert>
            </Snackbar>
          </Card>
        </AppBar>
      </MDBox>
    </DashboardLayout>
  );
}

AddClient.propTypes = {};
